'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { csrfTokenManager } from '@/lib/security/csrf/token-manager';
import { logger } from '@/utils/logger';
import { Turnstile } from 'next-turnstile';
import { useTheme } from '@/context/ThemeContext';
import { FormProps, FormField, FormFieldType } from './Form.types';
import { Calendar } from '@/components/Calendar';
import styles from './Form.module.css';

// Type for field renderer props
interface FieldRendererProps {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  error?: string;
  allErrors?: Record<string, string>; // New prop for custom renderers
  disabled?: boolean;
}

type FieldRenderer = (props: FieldRendererProps) => React.ReactNode;

// Default field renderers
const defaultRenderers: Record<FormFieldType, FieldRenderer> = {
  text: ({ field, value, onChange, error, disabled }) => (
    <div className={`${styles.field} ${field.className || ''}`}>
      {field.label && field.showLabel !== false && !field.hideLabel && (
        <label htmlFor={field.name} className={styles.label}>
          {field.label}
          {field.required && <span className={styles.required}>*</span>}
        </label>
      )}
      <input
        type="text"
        id={field.name}
        name={field.name}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        disabled={disabled || field.disabled}
        className={`${styles.input} ${error ? styles.inputError : ''}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${field.name}-error` : undefined}
      />
    </div>
  ),
  email: ({ field, value, onChange, error, disabled }) => (
    <div className={`${styles.field} ${field.className || ''}`}>
      {field.label && field.showLabel !== false && !field.hideLabel && (
        <label htmlFor={field.name} className={styles.label}>
          {field.label}
          {field.required && <span className={styles.required}>*</span>}
        </label>
      )}
      <input
        type="email"
        id={field.name}
        name={field.name}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        disabled={disabled || field.disabled}
        className={`${styles.input} ${error ? styles.inputError : ''}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${field.name}-error` : undefined}
      />
    </div>
  ),
  password: ({ field, value, onChange, error, disabled }) => (
    <div className={`${styles.field} ${field.className || ''}`}>
      {field.label && field.showLabel !== false && !field.hideLabel && (
        <label htmlFor={field.name} className={styles.label}>
          {field.label}
          {field.required && <span className={styles.required}>*</span>}
        </label>
      )}
      <input
        type="password"
        id={field.name}
        name={field.name}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        disabled={disabled || field.disabled}
        className={`${styles.input} ${error ? styles.inputError : ''}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${field.name}-error` : undefined}
      />
    </div>
  ),
  number: ({ field, value, onChange, error, disabled }) => (
    <div className={`${styles.field} ${field.className || ''}`}>
      {field.label && field.showLabel !== false && !field.hideLabel && (
        <label htmlFor={field.name} className={styles.label}>
          {field.label}
          {field.required && <span className={styles.required}>*</span>}
        </label>
      )}
      <input
        type="number"
        id={field.name}
        name={field.name}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        placeholder={field.placeholder}
        disabled={disabled || field.disabled}
        className={`${styles.input} ${error ? styles.inputError : ''}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${field.name}-error` : undefined}
      />
    </div>
  ),
  tel: ({ field, value, onChange, error, disabled }) => (
    <div className={`${styles.field} ${field.className || ''}`}>
      {field.label && field.showLabel !== false && !field.hideLabel && (
        <label htmlFor={field.name} className={styles.label}>
          {field.label}
          {field.required && <span className={styles.required}>*</span>}
        </label>
      )}
      <input
        type="tel"
        id={field.name}
        name={field.name}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        disabled={disabled || field.disabled}
        className={`${styles.input} ${error ? styles.inputError : ''}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${field.name}-error` : undefined}
        // Removed pattern and title to prevent native validation
      />
      {/* Removed the error message rendering here to prevent duplicates */}
    </div>
  ),
  textarea: ({ field, value, onChange, error, disabled }) => (
    <div className={`${styles.field} ${field.className || ''}`}>
      {field.label && field.showLabel !== false && !field.hideLabel && (
        <label htmlFor={field.name} className={styles.label}>
          {field.label}
          {field.required && <span className={styles.required}>*</span>}
        </label>
      )}
      <textarea
        id={field.name}
        name={field.name}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        disabled={disabled || field.disabled}
        className={`${styles.textarea} ${error ? styles.textareaError : ''}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${field.name}-error` : undefined}
        rows={4}
      />
    </div>
  ),
  select: ({ field, value, onChange, error, disabled }) => (
    <div className={`${styles.field} ${field.className || ''}`}>
      {field.label && field.showLabel !== false && !field.hideLabel && (
        <label htmlFor={field.name} className={styles.label}>
          {field.label}
          {field.required && <span className={styles.required}>*</span>}
        </label>
      )}
      <select
        id={field.name}
        name={field.name}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled || field.disabled}
        className={`${styles.select} ${error ? styles.selectError : ''}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${field.name}-error` : undefined}
      >
        <option value="">Select an option</option>
        {field.options?.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  ),
  checkbox: ({ field, value, onChange, error, disabled }) => {
    // Handle both single boolean and array values for multiple selection
    const isArrayMode = Array.isArray(value);
    const currentValues = isArrayMode ? (value as string[]) : [];

    const handleCheckboxChange = (optionValue: string | number, checked: boolean) => {
      const stringValue = String(optionValue);
      if (isArrayMode) {
        const newValues = checked
          ? [...currentValues, stringValue]
          : currentValues.filter(v => v !== stringValue);
        onChange(newValues);
      } else {
        onChange(checked);
      }
    };

    const handleOptionClick = (optionValue: string | number, e: React.MouseEvent) => {
      // Prevent double-clicking if user clicks on the input itself
      if ((e.target as HTMLElement).tagName === 'INPUT') return;

      const stringValue = String(optionValue);
      const isCurrentlyChecked = currentValues.includes(stringValue);

      // Calculate the new state optimistically for immediate visual feedback
      const willBeChecked = !isCurrentlyChecked;
      const newValues = willBeChecked
        ? [...currentValues, stringValue]
        : currentValues.filter(v => v !== stringValue);

      // Update parent state immediately
      if (isArrayMode) {
        onChange(newValues);
      } else {
        onChange(willBeChecked);
      }
    };

    if (field.options && field.options.length > 1) {
      // Multiple selection checkboxes
      return (
        <div className={`${styles.field} ${field.className || ''}`}>
          {field.label && field.showLabel !== false && !field.hideLabel && (
            <label className={styles.label}>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
          )}
          <div className={styles.checkboxWrapper}>
            {field.options.map((option) => {
              const isChecked = currentValues.includes(String(option.value));
              return (
                <div
                  key={option.value}
                  className={`${styles.checkboxOption} ${isChecked ? styles.checkboxOptionSelected : ''}`}
                  onClick={(e) => handleOptionClick(String(option.value), e)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOptionClick(String(option.value), e as any);
                    }
                  }}
                  aria-pressed={isChecked}
                >
                  <input
                    type="checkbox"
                    id={`${field.name}-${option.value}`}
                    name={field.name}
                    value={option.value}
                    checked={isChecked}
                    onChange={() => {}} // Remove onChange to prevent conflicts
                    disabled={disabled || field.disabled || option.disabled}
                    className={`${styles.checkbox} ${error ? styles.checkboxError : ''}`}
                    aria-invalid={!!error}
                    aria-describedby={error ? `${field.name}-error` : undefined}
                  />
                  <label htmlFor={`${field.name}-${option.value}`} className={styles.checkboxLabel}>
                    {option.label}
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      );
    } else {
      // Single checkbox: checkbox to the left of the label
      return (
        <div className={`${styles.field} ${field.className || ''}`}>
          {field.label && field.showLabel !== false && !field.hideLabel && (
            <label htmlFor={field.name} className={styles.label}>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
          )}
          <div className={`${styles.checkboxWrapper} ${styles.checkboxWrapperInline} ${field.className || ''}`}>
            <input
              type="checkbox"
              id={field.name}
              name={field.name}
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled || field.disabled}
              className={`${styles.checkbox} ${error ? styles.checkboxError : ''}`}
              aria-invalid={!!error}
              aria-describedby={error ? `${field.name}-error` : undefined}
            />
            {field.label && (
              <label htmlFor={field.name} className={styles.checkboxLabel}>
                {field.label}
              </label>
            )}
          </div>
        </div>
      );
    }
  },
  radio: ({ field, value, onChange, error, disabled }) => {
    const handleOptionClick = (optionValue: string | number, e: React.MouseEvent) => {
      // Prevent double-clicking if user clicks on the input itself
      if ((e.target as HTMLElement).tagName === 'INPUT') return;

      onChange(optionValue);
    };

    return (
      <div className={`${styles.field} ${field.className || ''}`}>
        {field.label && field.showLabel !== false && !field.hideLabel && (
          <label htmlFor={field.name} className={styles.label}>
            {field.label}
            {field.required && <span className={styles.required}>*</span>}
          </label>
        )}
        <div className={`${styles.radioGroup} ${field.className || ''}`}>
          {field.options?.map((option) => (
            <div
              key={option.value}
              className={`${styles.radioOption} ${value === option.value ? styles.radioOptionSelected : ''}`}
              onClick={(e) => handleOptionClick(String(option.value), e)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOptionClick(String(option.value), e as any);
                }
              }}
              aria-pressed={value === option.value}
            >
              <input
                type="radio"
                id={`${field.name}-${option.value}`}
                name={field.name}
                value={option.value}
                checked={value === option.value}
                onChange={() => {}} // Remove onChange to prevent conflicts
                disabled={disabled || field.disabled || option.disabled}
                className={`${styles.radio} ${error ? styles.radioError : ''}`}
                aria-invalid={!!error}
                aria-describedby={error ? `${field.name}-error` : undefined}
              />
              <label htmlFor={`${field.name}-${option.value}`} className={styles.radioLabel}>
                {option.label}
              </label>
            </div>
          ))}
        </div>
      </div>
    );
  },
  section: ({ field }) => (
    <div className={`${styles.sectionHeader} ${field.className || ''}`}>
      {field.label}
    </div>
  ),
  rating: ({ field, value, onChange, error, disabled, allErrors }) => {
    const hoverRating = useRef(0);
    const [forceUpdate, setForceUpdate] = useState(false);

    const ratingLabels = [
      'Poor',
      'Fair',
      'Good',
      'Very Good',
      'Excellent'
    ];

    const handleMouseEnter = (rating: number) => {
      if (disabled) return;
      hoverRating.current = rating;
      setForceUpdate(prev => !prev);
    };

    const handleMouseLeave = () => {
      hoverRating.current = 0;
      setForceUpdate(prev => !prev);
    };

    const currentRating = hoverRating.current || value || 0;
    const ratingText = currentRating ? `${currentRating} - ${ratingLabels[currentRating - 1]}` : 'Not rated';

    return (
      <div className={`${styles.field} ${field.className || ''}`} key={field.name}>
        {field.label && field.showLabel !== false && !field.hideLabel && (
          <label htmlFor={field.name} className={styles.label}>
            {field.label}
            {field.required && <span className={styles.required}>*</span>}
          </label>
        )}
        <div className={styles.ratingContainer}>
          <div className={styles.ratingLabel}>
            <span>1 - Poor</span>
            <span className={styles.ratingValue}>
              {ratingText}
            </span>
            <span>5 - Excellent</span>
          </div>
          <div className={styles.starRating}>
            {[1, 2, 3, 4, 5].map((rating) => (
              <button
                key={rating}
                type="button"
                className={`${styles.starButton} ${((hoverRating.current >= rating) || (value >= rating && hoverRating.current === 0)) ? styles.active : ''} ${error ? styles.starButtonError : ''}`}
                onClick={() => onChange(rating)}
                onMouseEnter={() => handleMouseEnter(rating)}
                onMouseLeave={handleMouseLeave}
                disabled={disabled || field.disabled}
                aria-label={`Rate ${rating} out of 5 stars - ${ratingLabels[rating - 1]}`}
                title={`Click to rate ${rating} out of 5 stars - ${ratingLabels[rating - 1]}`}
                data-rating={rating}
                data-force-update={forceUpdate}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(rating);
                  }
                }}
              >
                <svg className={styles.starIcon} viewBox="0 0 24 24">
                  <path d="M12 .587l3.668 7.568 8.332 1.151-6.064 5.828 1.48 8.279-7.416-3.967-7.417 3.967 1.481-8.279-6.064-5.828 8.332-1.151z"/>
                </svg>
              </button>
            ))}
          </div>
        </div>
        <input
          type="hidden"
          name={field.name}
          value={value || ''}
          aria-hidden="true"
        />
      </div>
    );
  },
  custom: ({ field, value, onChange, error, allErrors, disabled }) => {
    // Custom fields use the field's render prop
    if (field.render) {
      return field.render({
        field,
        value,
        onChange,
        error,
        allErrors,
        disabled,
      });
    }
    // Fallback for custom fields without render prop
    return (
      <div className={`${styles.field} ${field.className || ''}`}>
        {field.label && field.showLabel !== false && !field.hideLabel && (
          <label htmlFor={field.name} className={styles.label}>
            {field.label}
            {field.required && <span className={styles.required}>*</span>}
          </label>
        )}
        <div className={styles.customField}>
          Custom field: {field.name}
        </div>
      </div>
    );
  },
  date: ({ field, value, onChange, error, disabled }) => {
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(() => {
      if (value && typeof value === 'string') {
        const date = new Date(value);
        return isNaN(date.getTime()) ? undefined : date;
      }
      return value instanceof Date ? value : undefined;
    });

    const inputRef = useRef<HTMLInputElement>(null);
    const calendarRef = useRef<HTMLDivElement>(null);

    // Format date for display and input value (MM/DD/YYYY)
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    };

    // Parse date from string (MM/DD/YYYY)
    const parseDate = (dateString: string) => {
      if (!dateString) return undefined;
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? undefined : date;
    };

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      onChange(inputValue);

      // Try to parse the date as user types
      const parsedDate = parseDate(inputValue);
      setSelectedDate(parsedDate);
    };

    // Handle calendar date selection
    const handleCalendarChange = (date: Date) => {
      const formattedDate = formatDate(date);
      setSelectedDate(date);
      onChange(formattedDate);
      setIsCalendarOpen(false);
      inputRef.current?.focus();
    };

    // Handle input focus - toggle calendar
    const handleInputFocus = () => {
      setIsCalendarOpen(!isCalendarOpen);
    };

    // Handle escape key to close calendar
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && isCalendarOpen) {
        setIsCalendarOpen(false);
        inputRef.current?.focus();
      }
    }, [isCalendarOpen]);

    // Handle click outside to close calendar
    useEffect(() => {
      if (!isCalendarOpen) return;

      const handleMouseClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;

        // Only close if click is outside both input AND calendar
        // Don't close if clicking inside calendar
        if (calendarRef.current?.contains(target)) {
          return;
        }

        // Don't close if clicking inside input (let the click handler toggle)
        if (inputRef.current?.contains(target)) {
          return;
        }

        // Close calendar for clicks outside both input and calendar
        setIsCalendarOpen(false);
      };

      const handleTouchClickOutside = (e: TouchEvent) => {
        const target = e.target as Node;

        // Only close if touch is outside both input AND calendar
        // Don't close if touching inside calendar
        if (calendarRef.current?.contains(target)) {
          return;
        }

        // Don't close if touching inside input (let the click handler toggle)
        if (inputRef.current?.contains(target)) {
          return;
        }

        // Close calendar for touches outside both input and calendar
        setIsCalendarOpen(false);
      };

      // Add both mousedown and touchstart for better mobile support
      document.addEventListener('mousedown', handleMouseClickOutside);
      document.addEventListener('touchstart', handleTouchClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleMouseClickOutside);
        document.removeEventListener('touchstart', handleTouchClickOutside);
      };
    }, [isCalendarOpen]);

    const displayValue = value || '';
    const parsedDate = parseDate(displayValue);

    return (
      <div className={`${styles.field} ${styles.dateField} ${field.className || ''} ${error ? styles.inputError : ''}`}>
        {field.label && field.showLabel !== false && !field.hideLabel && (
          <label htmlFor={field.name} className={styles.label}>
            {field.label}
            {field.required && <span className={styles.required}>*</span>}
          </label>
        )}
        <div className={styles.datePickerWrapper}>
          <input
            ref={inputRef}
            type="text"
            id={field.name}
            name={field.name}
            value={value || ''}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={field.placeholder || 'MM/DD/YYYY'}
            disabled={disabled || field.disabled}
            className={`${styles.datePickerInput} ${error ? styles.inputError : ''}`}
            aria-invalid={!!error}
            aria-describedby={error ? `${field.name}-error` : undefined}
            data-placeholder={field.placeholder || 'MM/DD/YYYY'}
          />
          {isCalendarOpen && (
            <div
              ref={calendarRef}
              className={styles.datePickerCalendar}
              tabIndex={-1}
            >
              <Calendar
                value={parsedDate}
                onChange={handleCalendarChange}
                disabled={disabled || field.disabled}
                label=""
                className={`${error ? styles.calendarError : ''}`}
                // Start with a reasonable year range for birth dates
                minDate={new Date(1900, 0, 1)}
                maxDate={new Date()}
              />
            </div>
          )}
        </div>
      </div>
    );
  },
};

export const Form = ({
  title,
  description,
  fields,
  onSubmit,
  initialValues = {},
  submitButtonText = 'Submit',
  resetButtonText = 'Reset',
  showResetButton = true,
  className = '',
  formClassName = '',
  fieldClassName = '',
  buttonWrapperClassName = '',
  submitButtonClassName = '',
  resetButtonClassName = '',
  errorClassName = '',
  successClassName = '',
  loadingClassName = '',
  onSuccess,
  onError,
  onReset,
  onChange,
  onCancel,
  enableCSRF = false,
  enableRateLimit = false,
  rateLimitPurpose = 'form-submission',
  enableTurnstile = false,
  turnstileSiteKey,
  turnstileResetOnSubmit = true,
  successMessage = 'Form submitted successfully!',
  errorMessage = 'There was an error submitting the form. Please try again.',
  renderBeforeFields,
  renderAfterFields,
  renderSubmitButton,
  renderResetButton,
  renderLoading,
  renderSuccess,
  renderError,
}: FormProps) => {
  // Form state
  const [values, setValues] = useState<Record<string, any>>(initialValues);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success: boolean;
    message?: string;
    data?: any;
  } | null>(null);
  
  // CSRF and security state
  const [csrfToken, setCsrfToken] = useState<string>('');
  
  // Turnstile state
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [turnstileError, setTurnstileError] = useState<string>('');
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const [turnstileScriptError, setTurnstileScriptError] = useState(false);
  const { theme } = useTheme();

  // Refs
  const formRef = useRef<HTMLFormElement>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const turnstileRef = useRef<any>(null);
  const initialValuesRef = useRef(initialValues);

  // Handle Turnstile token change
  const handleTurnstileSuccess = useCallback((token: string) => {
    setTurnstileToken(token);
    setTurnstileError('');
    setTurnstileLoaded(true);
    // Clear any previous turnstile errors
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.turnstile;
      return newErrors;
    });
  }, []);

  // Handle Turnstile error
  const handleTurnstileError = useCallback((error: any) => {
    logger.error('Turnstile error', { error });
    setTurnstileError('Failed to load security verification. Please refresh the page and try again.');
    setTurnstileScriptError(true);
    // Clear any previous turnstile errors
    setErrors(prev => ({
      ...prev,
      turnstile: 'Security verification failed. Please refresh the page and try again.'
    }));
  }, []);

  // Handle Turnstile expiration
  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken('');
    setTurnstileError('Security verification expired. Please try again.');
    // Clear any previous turnstile errors
    setErrors(prev => ({
      ...prev,
      turnstile: 'Security verification expired. Please try again.'
    }));
  }, []);

  // Handle Turnstile script load error
  const handleTurnstileScriptError = useCallback(() => {
    logger.warn('Turnstile script failed to load');
    setTurnstileScriptError(true);
    setTurnstileLoaded(false);
  }, []);



  // Set up timeout to detect if Turnstile fails to load
  useEffect(() => {
    if (enableTurnstile && turnstileSiteKey && !turnstileLoaded && !turnstileScriptError) {
      // Check if we're in development with localhost port (which Turnstile doesn't support)
      const isDevWithPort = window.location.hostname === 'localhost' && window.location.port !== '';
      
      if (isDevWithPort) {
        logger.warn('Development environment detected with localhost port - Turnstile disabled');
        handleTurnstileScriptError();
        return;
      }

      // Debug logging
      logger.debug('Turnstile initialization', {
        enableTurnstile,
        turnstileSiteKey: turnstileSiteKey ? `${turnstileSiteKey.substring(0, 4)}...${turnstileSiteKey.substring(turnstileSiteKey.length - 4)}` : 'not-set',
        turnstileLoaded,
        turnstileScriptError,
        currentUrl: window.location.href,
        isDevWithPort
      });

      // Universal timeout
      const baseTimeout = 10000; // 10s base timeout
      const timeoutMs = baseTimeout;      

      const timeout = setTimeout(() => {
        // If Turnstile hasn't loaded after timeout, trigger retry
        if (!turnstileLoaded) {
          logger.warn(`Turnstile timeout after ${timeoutMs/1000}s`);
          handleTurnstileScriptError();
        }
      }, timeoutMs);

      return () => clearTimeout(timeout);
    }
  }, [enableTurnstile, turnstileSiteKey, turnstileLoaded, turnstileScriptError]);

  // Reset Turnstile widget
  const resetTurnstile = useCallback(() => {
    // next-turnstile doesn't have reset method, just clear token
    setTurnstileToken('');
    setTurnstileError('');
    // Clear Turnstile error from form errors
    setErrors(prev => {
      const { turnstile, ...rest } = prev;
      return rest;
    });
  }, [turnstileResetOnSubmit]);

  // Initialize CSRF token
  const generateNewCsrfToken = useCallback(async () => {
    try {
      const token = await csrfTokenManager.getToken();
      setCsrfToken(token);
      return token;
    } catch (error) {
      logger.error('Failed to generate CSRF token', { error });
      setSubmitStatus({
        success: false,
        message: 'Failed to initialize security token. Please refresh the page.'
      });
      return null;
    }
  }, []);
  
  // Cleanup CSRF token manager on unmount
  useEffect(() => {
    return () => {
      csrfTokenManager.cleanup();
    };
  }, []);

  // Update initialValuesRef when initialValues prop changes
  useEffect(() => {
    initialValuesRef.current = initialValues;
    
    // Only update values if the form is not dirty
    if (Object.keys(touched).length === 0) {
      setValues(initialValues);
    }
  }, [initialValues, touched]);

  // Validate a single field
  const validateField = useCallback(
    (field: FormField, value: any, allValues: Record<string, any> = values) => {
      if (!field.validation) return undefined;

      const { required, minLength, maxLength, pattern, validate } = field.validation;

      // Always call custom validate function if it exists
      if (validate) {
        const validationResult = validate(value, allValues);
        if (validationResult !== true) {
          return validationResult || 'Invalid value';
        }
      }

      // If no custom validation or custom validation passed, check other validations
      if (required) {
        let isFieldRequired = false;
        let requiredMessage = 'This field is required';
        
        // If required is a function, call it with all values
        if (typeof required === 'function') {
          const result = required(allValues);
          if (typeof result === 'string') {
            isFieldRequired = true;
            requiredMessage = result;
          } else {
            isFieldRequired = result;
          }
        } else if (typeof required === 'string') {
          isFieldRequired = true;
          requiredMessage = required;
        } else {
          isFieldRequired = required;
        }
        
        if (isFieldRequired) {
          if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
            return requiredMessage;
          }
        }
      }

      if (value === undefined || value === null || value === '') {
        return undefined; // Skip further validation for empty values if not required
      }

      if (minLength !== undefined) {
        const min = typeof minLength === 'number' ? minLength : minLength.value;
        const message =
          typeof minLength === 'object' ? minLength.message : `Must be at least ${min} characters`;
        if (String(value).length < min) {
          return message;
        }
      }

      if (maxLength !== undefined) {
        const max = typeof maxLength === 'number' ? maxLength : maxLength.value;
        const message =
          typeof maxLength === 'object' ? maxLength.message : `Must be at most ${max} characters`;
        if (String(value).length > max) {
          return message;
        }
      }

      if (pattern && !pattern.value.test(String(value))) {
        return pattern.message;
      }

      return undefined;
    },
    [values]
  );

  // Validate form fields
  const validateForm = useCallback(
    (fieldsToValidate: FormField[] = fields, valuesToValidate: Record<string, any> = values) => {
      const newErrors: Record<string, string> = {};
      let isValid = true;

      fieldsToValidate.forEach((field) => {
        if (field.type === 'section') return; // Skip section headers

        const error = validateField(field, valuesToValidate[field.name], valuesToValidate);
        if (error) {
          newErrors[field.name] = error;
          isValid = false;
        } else if (errors[field.name]) {
          // Only update if we need to clear an existing error
          setErrors((prevErrors) => {
            const { [field.name]: _, ...rest } = prevErrors;
            return rest;
          });
        }
      });

      if (Object.keys(newErrors).length > 0) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          ...newErrors,
        }));
      }

      return { isValid, errors: newErrors };
    },
    [errors, fields, validateField, values]
  );

  // Handle field change (only updates value, no validation)
  const handleChange = useCallback(
    (fieldName: string, value: any) => {
      // Update value
      setValues((prev) => ({
        ...prev,
        [fieldName]: value,
      }));
      
      // Call onChange prop if provided
      if (onChange) {
        onChange({ ...values, [fieldName]: value });
      }
    },
    [onChange, values]
  );
  
  // Handle field blur (validate when field loses focus)
  const handleBlur = useCallback((fieldName: string) => {
    // Mark field as touched
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    
    // Validate the field
    const field = fields.find(f => f.name === fieldName);
    if (field) {
      const error = validateField(field, values[fieldName], values);
      setErrors(prev => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[fieldName] = error;
        } else {
          delete newErrors[fieldName];
        }
        return newErrors;
      });
    }
  }, [fields, values, validateField]);

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Mark all fields as touched to show all errors
      const allTouched = fields.reduce((acc, field) => {
        if (field.type !== 'section') {
          acc[field.name] = true;
        }
        return acc;
      }, {} as Record<string, boolean>);
      setTouched(allTouched);

      // Validate form
      const validationResult = validateForm(fields, values);
      if (!validationResult.isValid && validationResult.errors) {
        setErrors(validationResult.errors);
        setIsSubmitting(false);
        // Scroll to top after a short delay to ensure errors are rendered
        setTimeout(() => {
          formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        return;
      }

      // Validate Turnstile if enabled - skip in development mode
      if (enableTurnstile && !turnstileToken && process.env.NODE_ENV !== 'development') {
        setErrors(prev => ({
          ...prev,
          turnstile: 'Please complete the verification challenge before submitting'
        }));
        setIsSubmitting(false);
        // Scroll to Turnstile widget or fallback message
        setTimeout(() => {
          const turnstileElement = document.querySelector('[data-turnstile]') || 
                                 document.querySelector('.turnstileFallback');
          turnstileElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        return;
      }

      // Prepare submission data
      const submissionData = { ...values };

      // Add Turnstile token to submission data if available
      if (enableTurnstile && turnstileToken && process.env.NODE_ENV !== 'development') {
        submissionData['cf-turnstile-response'] = turnstileToken;
      } else if (enableTurnstile && process.env.NODE_ENV === 'development') {
        // Add a mock token for development mode
        submissionData['cf-turnstile-response'] = 'development-mock-token';
      }

      // Get CSRF token if enabled
      let csrfToken: string | null = null;
      if (enableCSRF) {
        try {
          // Get a fresh token to ensure it's valid
          const token = await generateNewCsrfToken();
          if (!token) {
            throw new Error('Failed to get CSRF token');
          }
          csrfToken = token;
        } catch (error) {
          logger.error('CSRF token error', { error });
          setSubmitStatus({
            success: false,
            message: 'Security token error. Please refresh the page and try again.'
          });
          setIsSubmitting(false);
          return;
        }
      }

      // Use the form values from component state (submissionData already declared above)
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      // Add CSRF token to submission data and headers
      if (enableCSRF && csrfToken) {
        submissionData._csrf = csrfToken;
        headers['x-csrf-token'] = csrfToken;
      }

      // Add Turnstile token to submission data
      if (enableTurnstile && turnstileToken) {
        submissionData._turnstile = turnstileToken;
      }
        
        if (enableRateLimit) {
          submissionData._rateLimitPurpose = rateLimitPurpose;
        }

        // Pass headers to the onSubmit function
        const result = await onSubmit(submissionData, { headers });

        if (result.success) {
          const message = typeof successMessage === 'function' ? successMessage(result) : successMessage;
          // Store the complete response data in submitStatus
          setSubmitStatus({ 
            success: true, 
            message,
            data: result // Store the complete response data
          });
          if (onSuccess) onSuccess(result);
          
          // Reset Turnstile after successful submission
          resetTurnstile();
          
          // Reset form after successful submission only if not using custom success rendering
          if (result.resetForm !== false && !renderSuccess) {
            setValues(initialValues);
            setErrors({});
          }
        } else {
          // Handle form-level errors
          let message: string;

          if (result.errors) {
            setErrors(result.errors);
            // Check if this is a Turnstile validation error
            const isTurnstileError = result.errors.turnstile || 
                                   result.message?.toLowerCase().includes('turnstile') ||
                                   result.message?.toLowerCase().includes('verification') ||
                                   result.message?.toLowerCase().includes('captcha');
            
            if (isTurnstileError) {
              // Clear the current Turnstile token and reset widget for retry
              setTurnstileToken('');
              resetTurnstile();
              message = result.message || 'Security verification failed. Please complete the challenge again.';
              
              // Scroll to Turnstile widget for user to retry
              setTimeout(() => {
                const turnstileElement = document.querySelector('[data-turnstile]') || 
                                       document.querySelector('.turnstileFallback');
                turnstileElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 100);
            } else {
              // If there are specific field errors, don't show a generic submitStatus message.
              // The individual field errors will be displayed by renderField.
              message = result.message || 'Validation failed. Please check the form for details.';
            }
            setSubmitStatus({ success: false, message });
          } else {
            // Handle general form errors (e.g., CSRF, rate limit, Turnstile)
            const isCsrfError = result.message?.toLowerCase().includes('csrf');
            const isTurnstileError = result.message?.toLowerCase().includes('turnstile') ||
                                   result.message?.toLowerCase().includes('verification') ||
                                   result.message?.toLowerCase().includes('captcha');
            
            if (isTurnstileError) {
              // Clear the current Turnstile token and reset widget for retry
              setTurnstileToken('');
              resetTurnstile();
              message = result.message || 'Security verification failed. Please complete the challenge again.';
              
              // Scroll to Turnstile widget for user to retry
              setTimeout(() => {
                const turnstileElement = document.querySelector('[data-turnstile]') || 
                                       document.querySelector('.turnstileFallback');
                turnstileElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 100);
            } else {
              const defaultMessage = isCsrfError 
                ? 'Session expired. Please refresh the page and try again.'
                : (typeof errorMessage === 'function' ? errorMessage(new Error('Form submission failed')) : errorMessage);
                
              message = result.message || defaultMessage;
            }
            setSubmitStatus({ success: false, message });
          }
          
          if (onError) onError(new Error(message));
        }
        // Crucial: Scroll to the form after *any* submission outcome
        requestAnimationFrame(() => {
          formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      } catch (error) {
        logger.error('Form submission error', { 
          error,
          formValues: Object.keys(values).reduce((acc, key) => {
            // Redact sensitive information
            const lowerKey = key.toLowerCase();
            if (lowerKey.includes('password') || lowerKey.includes('ssn') || lowerKey.includes('credit')) {
              acc[key] = '***REDACTED***';
            } else {
              acc[key] = values[key];
            }
            return acc;
          }, {} as Record<string, any>)
        });
        
        const isCsrfError = error instanceof Error && error.message.toLowerCase().includes('csrf');
        const isTurnstileError = error instanceof Error && (
          error.message.toLowerCase().includes('turnstile') ||
          error.message.toLowerCase().includes('verification') ||
          error.message.toLowerCase().includes('captcha')
        );
        
        if (isTurnstileError) {
          // Clear the current Turnstile token and reset widget for retry
          setTurnstileToken('');
          resetTurnstile();
          
          // Scroll to Turnstile widget for user to retry
          setTimeout(() => {
            const turnstileElement = document.querySelector('[data-turnstile]') || 
                                   document.querySelector('.turnstileFallback');
            turnstileElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        }
        
        const message = isCsrfError 
          ? 'Session expired. Please refresh the page and try again.'
          : (isTurnstileError 
            ? 'Security verification failed. Please complete the challenge again.'
            : (error instanceof Error ? error.message : 'An unknown error occurred'));
          
        setSubmitStatus({ success: false, message });
        if (onError) onError(error instanceof Error ? error : new Error(message));
        
        // Crucial: Scroll to the form after catch block
        requestAnimationFrame(() => {
          formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      } finally {
        setIsSubmitting(false);
        // Reset Turnstile on submission completion if enabled
        if (!submitStatus?.success && enableTurnstile) {
          resetTurnstile();
        }
      }
    },
    [
      values,
      validateForm,
      enableCSRF,
      csrfToken,
      enableRateLimit,
      rateLimitPurpose,
      enableTurnstile,
      turnstileToken,
      onSubmit,
      successMessage,
      errorMessage,
      onSuccess,
      onError,
      formRef, // Include formRef in dependency array
      resetTurnstile,
    ]
  );

  // Handle form reset
  const handleReset = useCallback(() => {
    // Call onReset first to allow parent component to update initialValues
    if (onReset) onReset();

    // Use requestAnimationFrame to ensure the parent component has updated initialValues
    requestAnimationFrame(() => {
      setValues(initialValuesRef.current);
      setErrors({});
      setTouched({});
      setSubmitStatus(null);
    });
  }, [onReset]);

  // Render a single form field
  const renderField = (field: FormField) => {
    // Check if field should be hidden based on static hidden property or showWhen condition
    if (field.hidden && !field.showWhen) return null;

    // Check showWhen condition if provided - if field should be hidden, don't render
    if (field.showWhen && !field.showWhen(values)) return null;

    const error = errors[field.name];
    const fieldValue = values[field.name] ?? field.defaultValue;
    const isDisabled = isSubmitting || field.disabled;
    
    // Determine if field is required (supports function-based required in validation)
    let isRequired = field.required;
    // For fields with showWhen condition, check if they're currently visible and should be marked as required
    if (!isRequired && field.showWhen && field.showWhen(values)) {
      // If the field is visible due to showWhen, check if it has validation that makes it required
      if (field.validation?.validate) {
        // Run a quick validation with empty value to see if it's required
        const validationResult = field.validation.validate('', values);
        isRequired = validationResult !== true;
      }
    }

    // Use custom renderer if provided, otherwise use default renderer for the field type
    const renderer = field.render || defaultRenderers[field.type] || defaultRenderers.text;

    return (
      <div
        key={field.name}
        className={`${styles.field} ${field.wrapperClassName || ''} ${fieldClassName || ''}`.trim()}
      >
        {/* Removed redundant label rendering here */}

        <div className={`${fieldClassName || ''} ${field.className || ''}`.trim() || undefined}>
          {renderer({
            field: { 
              ...field, 
              showLabel: field.showLabel ?? true,
              required: isRequired
            },
            value: fieldValue,
            onChange: (value: any) => handleChange(field.name, value),
            onBlur: () => handleBlur(field.name),
            error: field.type === 'custom' ? undefined : (touched[field.name] ? error : undefined),
            allErrors: field.type === 'custom' ? 
              Object.fromEntries(
                Object.entries(errors).filter(([_, value]) => value !== undefined)
              ) as Record<string, string> : 
              undefined,
            disabled: isDisabled,
          })}
        </div>

        {field.description && !error && (
          <div className={styles.description}>{field.description}</div>
        )}

        {error && (
          <div id={`${field.name}-error`} className={`${styles.errorMessage} ${errorClassName}`}>
            {error}
          </div>
        )}
      </div>
    );
  };

  // Handle loading state
  if (isSubmitting && renderLoading) {
    return <div className={loadingClassName}>{renderLoading()}</div>;
  }

  // Handle form status changes
  useEffect(() => {
    // This useEffect is now primarily for generating CSRF tokens or other side effects.
    // The scrolling logic has been moved to the handleSubmit for more precise control.
    if (enableCSRF && submitStatus?.message?.includes('CSRF')) {
      const regenerateToken = async () => {
        try {
          const token = await csrfTokenManager.refreshToken();
          if (token) {
            setCsrfToken(token);
          }
        } catch (error) {
          logger.error('Failed to regenerate CSRF token', { error });
        }
      };
      regenerateToken();
    }
  }, [enableCSRF, submitStatus, initialValues]); // Added submitStatus to dependencies

  // Render the form
  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      onReset={handleReset}
      className={`${formClassName || styles.form} ${className}`}
      style={formClassName ? undefined : { display: 'flex', flexDirection: 'column', gap: '2rem' }}
      noValidate
    >
      {title && <h2 className={styles.formTitle}>{title}</h2>}
      {description && <p className={styles.formDescription}>{description}</p>}
      
      <div ref={messageContainerRef}> {/* This wrapper is restored and used for internal reference */}
        {/* Form-level errors (submission or consolidated validation errors) */}
        {(submitStatus && !submitStatus.success && Object.keys(errors).length === 0) && (
          <div className={`${styles.alert} ${styles.alertError} ${errorClassName}`} role="alert">
            {submitStatus.message?.includes('CSRF') || submitStatus.message?.includes('token')
              ? 'Your session has expired. Please refresh the page and try again.'
              : typeof submitStatus.message === 'string' 
                ? submitStatus.message 
                : typeof errorMessage === 'function' 
                  ? errorMessage(new Error(String(submitStatus?.message || 'Form submission failed'))) 
                  : errorMessage || 'An error occurred'}
          </div>
        )}
        {Object.keys(errors).length > 0 && (
          <div className={`${styles.alert} ${styles.alertError} ${errorClassName}`} role="alert">
            <p>Please correct the following errors:</p>
            <ul>
              {Object.entries(errors).map(([fieldName, errorMessage]) => (
                <li key={fieldName}>{fields.find(f => f.name === fieldName)?.label || fieldName}: {errorMessage}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Success message */}
        {submitStatus?.success && renderSuccess ? (
          renderSuccess(submitStatus.message || 'Form submitted successfully!')
        ) : submitStatus?.success && (
          <div className={`${styles.alert} ${styles.alertSuccess} ${successClassName}`}>
            {submitStatus.message || 'Form submitted successfully!'}
          </div>
        )}
      </div>
      
      {isSubmitting && (
        <div className={`${styles.loadingOverlay} ${loadingClassName}`}>
          {renderLoading ? renderLoading() : <div className={styles.loadingSpinner} />}
        </div>
      )}
      
      {/* CSRF Token */}
      {enableCSRF && csrfToken && (
        <input type="hidden" name="_csrf" value={csrfToken} />
      )}
      {/* Rate limit purpose */}
      {enableRateLimit && rateLimitPurpose && (
        <input type="hidden" name="_rateLimitPurpose" value={rateLimitPurpose} />
      )}
      {/* Before fields */}
      {renderBeforeFields && renderBeforeFields()}
      {/* Form fields */}
      {fields.map(renderField)}
      {/* After fields */}
      {renderAfterFields && renderAfterFields()}
      {/* Turnstile widget - moved to bottom */}
      {enableTurnstile && turnstileSiteKey && (
        <div className={`${styles.field} ${styles.turnstileField}`}>
          <div className={styles.turnstileWrapper}>
            {process.env.NODE_ENV === 'development' ? (
              <div style={{
                width: '100%',
                maxWidth: '300px',
                margin: '0 auto',
                padding: '1rem',
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                color: '#4a5568',
                fontSize: '0.875rem',
                textAlign: 'center'
              }}>
                <div>🔒 Security Check (Development Mode)</div>
                <div style={{
                  fontSize: '0.75rem',
                  color: '#718096'
                }}>
                  In production, this will be replaced with Cloudflare Turnstile
                </div>
              </div>
            ) : (
              <Turnstile
                siteKey={turnstileSiteKey}
                action="contact_form"
                appearance="always"
                theme={theme === 'dark' ? 'dark' : 'light'}
                language="en"
                responseField={false}
                size="normal"
                onVerify={handleTurnstileSuccess}
                onError={handleTurnstileError}
                onExpire={handleTurnstileExpire}
                onLoad={() => {
                  logger.debug('Turnstile widget loaded successfully');
                  if (errors['turnstile']) {
                    setErrors(prev => ({
                      ...prev,
                      turnstile: undefined
                    }));
                  }
                }}
                tabIndex={0}
                aria-label="Security verification"
              />
            )}
            
            {/* Loading state */}
            {!turnstileLoaded && !turnstileScriptError && (
                  <div 
                    style={{ 
                      fontSize: '0.75rem', 
                      color: 'var(--form-text-secondary, #718096)',
                      textAlign: 'center',
                      marginTop: '0.5rem',
                      width: '100%'
                    }}
                  >
                    🛡️ Loading security verification...
                  </div>
                )}
                
                {errors['turnstile'] && !turnstileScriptError && (
                  <div id="turnstile-error" className={`${styles.errorMessage} ${errorClassName}`}>
                    {errors['turnstile']}
                  </div>
                )}
              </div>
            </div>
          )}
      
      {/* Form actions */}
      <div className={`form-buttons ${buttonWrapperClassName || ''}`}>
        {showResetButton &&
          (renderResetButton ? (
            renderResetButton({
              disabled: isSubmitting,
              onClick: handleReset,
              className: resetButtonClassName,
            })
          ) : (
            <button
              type="button"
              onClick={handleReset}
              disabled={isSubmitting || submitStatus?.success}
              className={`btn btn--secondary ${resetButtonClassName}`}
              aria-label={`Reset form and clear all fields`}
              title="Clear all form fields and start over"
            >
              {resetButtonText}
            </button>
          ))}

        {!submitStatus?.success && (renderSubmitButton ? (
          renderSubmitButton({
            disabled: isSubmitting,
            loading: isSubmitting,
            onClick: (e: React.MouseEvent) => {
              e.preventDefault();
              handleSubmit(e as any);
            },
            className: submitButtonClassName,
          })
        ) : (
          <button
            type="submit"
            disabled={isSubmitting}
            className={`btn btn--primary ${submitButtonClassName}`}
            aria-label={isSubmitting ? `Submitting form, please wait` : `Submit form`}
            title={isSubmitting ? "Form is being submitted, please wait" : "Submit the form"}
          >
            {isSubmitting ? 'Submitting...' : submitButtonText}
          </button>
        ))}
      </div>
    </form>
  );
}

export default Form;




